import React, { useEffect, useState } from "react";
import { Trophy, Lock, Plus, Users, Sparkles, X, Check, UserMinus, Ban, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api, { fmtINR, fmtDate } from "@/lib/api";

export default function AdminLuckyDraw() {
  const [groups, setGroups] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showMembers, setShowMembers] = useState(null);  // group object
  const [memberData, setMemberData] = useState({ members: [], blocked: [], customers: [] });
  const [tab, setTab] = useState("available");
  const [form, setForm] = useState({ name: "", draw_date: "", max_members: 50, reward_percent: 50 });

  const load = () => api.get("/admin/lucky-draws").then((r) => setGroups(r.data));
  useEffect(() => { load(); }, []);

  const loadMembers = async (gid) => {
    const r = await api.get(`/admin/lucky-draws/${gid}/members`);
    const enrolled = r.data.customers.filter((c) => c.is_member);
    const blocked = r.data.customers.filter((c) => c.is_blocked && !c.is_member);
    const available = r.data.customers.filter((c) => !c.is_member && !c.is_blocked);
    setMemberData({ enrolled, blocked, available, all: r.data.customers, group: r.data.group });
  };

  const openMembers = async (g) => {
    setShowMembers(g);
    setTab("available");
    await loadMembers(g.id);
  };

  const create = async () => {
    if (!form.name || !form.draw_date) return toast.error("Name & date required");
    try {
      await api.post("/admin/lucky-draws", { ...form, max_members: parseInt(form.max_members), reward_percent: parseFloat(form.reward_percent) });
      toast.success("Group created");
      setShowCreate(false); load();
    } catch { toast.error("Failed"); }
  };

  const lock = async (id) => { await api.post(`/admin/lucky-draws/${id}/lock`); toast.success("Locked"); load(); };
  const pick = async (id) => { const r = await api.post(`/admin/lucky-draws/${id}/pick-winner`); toast.success(`Winner: ${r.data.winner} (${fmtINR(r.data.reward)})`); load(); };

  const addMember = async (cid) => {
    try { await api.post(`/admin/lucky-draws/${showMembers.id}/members`, { customer_id: cid }); toast.success("Added"); loadMembers(showMembers.id); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const removeMember = async (cid) => {
    try { await api.delete(`/admin/lucky-draws/${showMembers.id}/members/${cid}`); toast.success("Removed"); loadMembers(showMembers.id); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const blockMember = async (cid) => {
    try { await api.post(`/admin/lucky-draws/${showMembers.id}/block/${cid}`); toast.success("Blocked from group"); loadMembers(showMembers.id); load(); }
    catch (e) { toast.error("Failed"); }
  };
  const unblockMember = async (cid) => {
    try { await api.post(`/admin/lucky-draws/${showMembers.id}/unblock/${cid}`); toast.success("Unblocked"); loadMembers(showMembers.id); load(); }
    catch (e) { toast.error("Failed"); }
  };

  const visible = tab === "available" ? (memberData.available || [])
                : tab === "enrolled" ? (memberData.enrolled || [])
                : (memberData.blocked || []);

  return (
    <div data-testid="admin-luckydraw-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">Lucky Draw Management</h1>
          <p className="text-sm text-slate-500">Create groups, manage members & pick winners</p>
        </div>
        <button data-testid="create-group-btn" onClick={() => setShowCreate(true)} className="inline-flex items-center gap-2 bg-tenant-primary text-white px-4 py-2.5 rounded-xl text-sm font-bold">
          <Plus className="w-4 h-4" /> Create Group
        </button>
      </div>

      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        {groups.map((g) => (
          <div key={g.id} data-testid={`ld-group-${g.id}`} className="bg-white border border-slate-100 rounded-2xl p-5 card-shadow">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-500" /><span className="font-extrabold tracking-tight">{g.name}</span></div>
              <span className={`text-[10px] uppercase font-bold px-2.5 py-1 rounded-full ${g.status === "open" ? "bg-emerald-50 text-emerald-700" : g.status === "locked" ? "bg-amber-50 text-amber-700" : "bg-violet-50 text-violet-700"}`}>{g.status}</span>
            </div>
            <div className="text-xs text-slate-500 mt-2">Draw: <b>{fmtDate(g.draw_date)}</b> · Reward: {g.reward_percent}%</div>
            <div className="mt-3 flex items-center gap-1 text-sm"><Users className="w-4 h-4 text-slate-400" /><b>{g.member_count}/{g.max_members}</b> members</div>
            {g.winner_name && <div className="mt-2 text-sm bonus-wallet-grad text-white rounded-xl p-2.5 text-center"><b>Winner:</b> {g.winner_name} · {fmtINR(g.winner_reward || 0)}</div>}
            <div className="mt-3 flex flex-wrap gap-2">
              {g.status === "open" && <>
                <button data-testid={`manage-members-${g.id}`} onClick={() => openMembers(g)} className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 font-bold">Manage Members</button>
                <button data-testid={`lock-${g.id}`} onClick={() => lock(g.id)} className="text-xs px-3 py-1.5 rounded-lg bg-amber-500 text-white font-bold inline-flex items-center gap-1"><Lock className="w-3 h-3" />Lock</button>
              </>}
              {g.status === "locked" && (
                <>
                <button data-testid={`view-members-${g.id}`} onClick={() => openMembers(g)} className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 font-bold">View Members</button>
                <button data-testid={`pick-winner-${g.id}`} onClick={() => pick(g.id)} className="text-xs px-3 py-1.5 rounded-lg bg-violet-600 text-white font-bold inline-flex items-center gap-1"><Sparkles className="w-3 h-3" />Pick Winner</button>
                </>
              )}
            </div>
          </div>
        ))}
        {groups.length === 0 && <div className="text-center text-slate-400 col-span-full py-12">No groups yet.</div>}
      </div>

      {showCreate && (
        <Modal onClose={() => setShowCreate(false)} title="Create Lucky Draw Group">
          <Input label="Group Name" testId="ld-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
          <Input label="Draw Date" testId="ld-date" value={form.draw_date} onChange={(v) => setForm({ ...form, draw_date: v })} type="date" />
          <div className="grid grid-cols-2 gap-2">
            <Input label="Max Members" testId="ld-max" value={form.max_members} onChange={(v) => setForm({ ...form, max_members: v })} type="number" />
            <Input label="Reward %" testId="ld-reward" value={form.reward_percent} onChange={(v) => setForm({ ...form, reward_percent: v })} type="number" />
          </div>
          <button data-testid="confirm-create-group" onClick={create} className="w-full bg-tenant-primary text-white py-3 rounded-xl font-bold mt-3">Create</button>
        </Modal>
      )}

      {showMembers && (
        <Modal onClose={() => { setShowMembers(null); }} title={`Members · ${showMembers.name}`} wide>
          <div className="grid grid-cols-3 gap-1 bg-slate-100 rounded-xl p-1 mb-3">
            <button data-testid="tab-available" onClick={() => setTab("available")} className={`py-2 rounded-lg text-xs font-bold ${tab === "available" ? "bg-white shadow text-tenant-primary" : "text-slate-500"}`}>Available ({memberData.available?.length || 0})</button>
            <button data-testid="tab-enrolled" onClick={() => setTab("enrolled")} className={`py-2 rounded-lg text-xs font-bold ${tab === "enrolled" ? "bg-white shadow text-emerald-700" : "text-slate-500"}`}>Enrolled ({memberData.enrolled?.length || 0})</button>
            <button data-testid="tab-blocked" onClick={() => setTab("blocked")} className={`py-2 rounded-lg text-xs font-bold ${tab === "blocked" ? "bg-white shadow text-rose-700" : "text-slate-500"}`}>Blocked ({memberData.blocked?.length || 0})</button>
          </div>

          <div className="max-h-96 overflow-y-auto divide-y rounded-lg border border-slate-100">
            {visible.map((c) => (
              <div key={c.id} data-testid={`member-row-${c.id}`} className="p-3 flex justify-between items-center hover:bg-slate-50">
                <div>
                  <div className="font-bold text-sm">{c.full_name}</div>
                  <div className="text-[11px] text-slate-400">+91 {c.phone}</div>
                </div>
                <div className="flex items-center gap-1.5">
                  {tab === "available" && (
                    <>
                      <button data-testid={`add-mem-${c.id}`} onClick={() => addMember(c.id)} className="text-[11px] bg-emerald-50 text-emerald-700 font-bold px-2.5 py-1.5 rounded-lg inline-flex items-center gap-1 hover:bg-emerald-100">
                        <Plus className="w-3 h-3" /> Add
                      </button>
                      <button data-testid={`block-mem-${c.id}`} onClick={() => blockMember(c.id)} title="Block from joining" className="text-[11px] bg-rose-50 text-rose-600 font-bold px-2 py-1.5 rounded-lg inline-flex items-center hover:bg-rose-100">
                        <Ban className="w-3 h-3" />
                      </button>
                    </>
                  )}
                  {tab === "enrolled" && (
                    <>
                      <span className="text-[10px] bg-emerald-100 text-emerald-700 font-bold px-2 py-1 rounded-full inline-flex items-center gap-1">
                        <Check className="w-3 h-3" /> JOINED
                      </span>
                      <button data-testid={`remove-mem-${c.id}`} onClick={() => removeMember(c.id)} title="Remove from group" className="text-[11px] bg-rose-50 text-rose-600 font-bold px-2.5 py-1.5 rounded-lg inline-flex items-center gap-1 hover:bg-rose-100">
                        <UserMinus className="w-3 h-3" /> Remove
                      </button>
                      <button data-testid={`block-from-enrolled-${c.id}`} onClick={() => blockMember(c.id)} title="Block from re-joining" className="text-[11px] bg-rose-50 text-rose-600 font-bold px-2 py-1.5 rounded-lg inline-flex items-center hover:bg-rose-100">
                        <Ban className="w-3 h-3" />
                      </button>
                    </>
                  )}
                  {tab === "blocked" && (
                    <button data-testid={`unblock-mem-${c.id}`} onClick={() => unblockMember(c.id)} className="text-[11px] bg-blue-50 text-tenant-primary font-bold px-2.5 py-1.5 rounded-lg inline-flex items-center gap-1 hover:bg-blue-100">
                      <RotateCcw className="w-3 h-3" /> Unblock
                    </button>
                  )}
                </div>
              </div>
            ))}
            {visible.length === 0 && <div className="text-center text-slate-400 text-sm py-8">No customers in this tab.</div>}
          </div>
        </Modal>
      )}
    </div>
  );
}

const Modal = ({ children, onClose, title, wide }) => (
  <div className="fixed inset-0 bg-slate-900/50 backdrop-blur grid place-items-center z-50 p-4" onClick={onClose}>
    <div className={`bg-white rounded-3xl p-6 ${wide ? "max-w-lg" : "max-w-sm"} w-full max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
      <div className="flex justify-between items-center mb-3">
        <div className="font-extrabold text-lg">{title}</div>
        <button onClick={onClose}><X className="w-4 h-4" /></button>
      </div>
      {children}
    </div>
  </div>
);

const Input = ({ label, testId, value, onChange, type = "text" }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">{label}</label>
    <input data-testid={testId} type={type} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full border-2 border-slate-200 rounded-xl px-3 py-2 outline-none focus:border-tenant-primary" />
  </div>
);
